import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CATEGORIES, ACCESSIBILITY_AREAS, NOMINATOR_CONNECTIONS } from "@/data/constants";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Upload, X, Plus } from "lucide-react";
import CategoryIcon from "@/components/CategoryIcon";

const emptyForm = {
  venue_name: "", category: "", area: "", address: "", postcode: "", country: "United Kingdom",
  phone: "", email: "", website: "", opening_hours: "", description: "",
  lat: "", lng: "",
  accessibility_description: "", inclusive_experience: "",
  evidence_description: "",
  has_visited: null, experience_description: "",
  stand_out: "", reason_for_inclusion: "",
  nominator_connection: "", nominator_can_contact: null,
};

export default function Nominate() {
  const navigate = useNavigate();
  const [features, setFeatures] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [links, setLinks] = useState([""]);
  const [consent, setConsent] = useState({ accuracy: false, understanding: false, review: false });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/meta/features").then(r => setFeatures(r.data));
    api.get("/meta/areas").then(r => setAreas(r.data));
  }, []);

  const toggle = (list, setter, val) =>
    setter(list.includes(val) ? list.filter(x => x !== val) : [...list, val]);

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos(p => [...p, data.path]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const allConsent = consent.accuracy && consent.understanding && consent.review;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category || !form.area) { toast.error("Please choose category and area"); return; }
    if (!allConsent) { toast.error("Please accept all consent statements"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        features: selectedFeatures,
        accessibility_areas: selectedAreas,
        evidence_links: links.map(l => l.trim()).filter(Boolean),
        photo_paths: photos,
        lat: form.lat ? parseFloat(form.lat) : null,
        lng: form.lng ? parseFloat(form.lng) : null,
        email: form.email || null,
        consent_accepted: true,
      };
      const { data } = await api.post("/venues/nominate", payload);
      toast.success("Thanks! Your nomination is pending review.");
      navigate(`/venue/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const catObj = CATEGORIES.find(c => c.slug === form.category);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <h1 className="font-heading text-4xl sm:text-5xl font-bold text-gray-900">Nominate a place</h1>
      <p className="mt-3 text-lg text-gray-700">Tell us about a venue you'd like reviewed for accessibility. All fields marked <span className="text-blue-700 font-semibold">*</span> are required.</p>

      <form onSubmit={submit} className="mt-10 space-y-6">
        {/* 1. About the Venue */}
        <SectionCard title="1. About the Venue">
          <div>
            <Label>Venue / Organisation Name <Star/></Label>
            <Input required value={form.venue_name} onChange={e => set("venue_name", e.target.value)}
              className="mt-1 h-12 border-2 border-gray-300 focus:border-blue-600" data-testid="nom-name"/>
          </div>
          <div>
            <Label>Category <Star/></Label>
            <Select value={form.category} onValueChange={v => set("category", v)}>
              <SelectTrigger className="mt-1 h-12 border-2 border-gray-300" data-testid="nom-category">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => <SelectItem key={c.slug} value={c.slug}>{c.short}</SelectItem>)}
              </SelectContent>
            </Select>
            {catObj && (
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                <CategoryIcon slug={catObj.slug} className="w-4 h-4"/> {catObj.blurb}
              </div>
            )}
          </div>
          <div>
            <Label>Area <Star/></Label>
            <Select value={form.area} onValueChange={v => set("area", v)}>
              <SelectTrigger className="mt-1 h-12 border-2 border-gray-300" data-testid="nom-area">
                <SelectValue placeholder="Select an area" />
              </SelectTrigger>
              <SelectContent>
                {areas.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Address <Star/></Label>
              <Input required value={form.address} onChange={e => set("address", e.target.value)}
                className="mt-1 h-12 border-2 border-gray-300 focus:border-blue-600" data-testid="nom-address"/>
            </div>
            <div>
              <Label>Postcode</Label>
              <Input value={form.postcode} onChange={e => set("postcode", e.target.value)}
                className="mt-1 h-12 border-2 border-gray-300 focus:border-blue-600" data-testid="nom-postcode"/>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={e => set("phone", e.target.value)} className="mt-1 h-12 border-2 border-gray-300"/>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={e => set("email", e.target.value)} className="mt-1 h-12 border-2 border-gray-300"/>
            </div>
            <div>
              <Label>Website</Label>
              <Input value={form.website} onChange={e => set("website", e.target.value)} className="mt-1 h-12 border-2 border-gray-300"/>
            </div>
          </div>
          <div>
            <Label>Opening hours</Label>
            <Input value={form.opening_hours} onChange={e => set("opening_hours", e.target.value)}
              placeholder="Mon–Fri 9:00–17:00" className="mt-1 h-12 border-2 border-gray-300"/>
          </div>
          <div>
            <Label>Short description</Label>
            <Textarea rows={3} value={form.description} onChange={e => set("description", e.target.value)}
              className="mt-1 border-2 border-gray-300" data-testid="nom-description"/>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Latitude (optional)</Label>
              <Input value={form.lat} onChange={e => set("lat", e.target.value)} placeholder="51.5544" className="mt-1 h-12 border-2 border-gray-300"/>
            </div>
            <div>
              <Label>Longitude (optional)</Label>
              <Input value={form.lng} onChange={e => set("lng", e.target.value)} placeholder="-1.7823" className="mt-1 h-12 border-2 border-gray-300"/>
            </div>
          </div>
        </SectionCard>

        {/* 2. Accessibility Information */}
        <SectionCard title="2. Accessibility Information">
          <div>
            <Label>Which accessibility areas does this venue support? <Star/></Label>
            <p className="text-sm text-gray-600 mt-1">Select all that apply.</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ACCESSIBILITY_AREAS.map(a => (
                <label key={a} className={`flex items-start gap-2 border-2 rounded-lg px-3 py-2 cursor-pointer ${selectedAreas.includes(a) ? "border-blue-600 bg-blue-50" : "border-gray-200"}`}>
                  <Checkbox checked={selectedAreas.includes(a)} onCheckedChange={() => toggle(selectedAreas, setSelectedAreas, a)}
                    data-testid={`acc-area-${a.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}/>
                  <span className="text-sm leading-snug">{a}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label>Please describe the accessibility features and support available at this venue. <Star/></Label>
            <Textarea required rows={4} value={form.accessibility_description}
              onChange={e => set("accessibility_description", e.target.value)}
              className="mt-1 border-2 border-gray-300" data-testid="nom-acc-desc"/>
          </div>
          <div>
            <Label>How does the venue make the experience genuinely accessible and inclusive for disabled people? <Star/></Label>
            <Textarea required rows={4} value={form.inclusive_experience}
              onChange={e => set("inclusive_experience", e.target.value)}
              className="mt-1 border-2 border-gray-300" data-testid="nom-inclusive"/>
          </div>
          <div>
            <Label>Accessible features to highlight</Label>
            <p className="text-sm text-gray-600 mt-1">Tick anything you know applies (used for filters and the venue profile).</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {features.map(f => (
                <label key={f} className={`flex items-center gap-2 border-2 rounded-lg px-3 py-2 cursor-pointer ${selectedFeatures.includes(f) ? "border-blue-600 bg-blue-50" : "border-gray-200"}`}>
                  <Checkbox checked={selectedFeatures.includes(f)} onCheckedChange={() => toggle(selectedFeatures, setSelectedFeatures, f)}/>
                  <span className="text-sm">{f}</span>
                </label>
              ))}
            </div>
          </div>
        </SectionCard>

        {/* 3. Evidence */}
        <SectionCard title="3. Evidence">
          <div>
            <Label>Photos <Star/></Label>
            <div className="mt-2 flex items-center gap-3">
              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-gray-900 cursor-pointer hover:bg-gray-50" data-testid="upload-photo-label">
                <Upload className="w-4 h-4"/>{uploading ? "Uploading…" : "Add photo"}
                <input type="file" accept="image/*" className="hidden"
                  onChange={e => e.target.files?.[0] && uploadPhoto(e.target.files[0])} data-testid="upload-photo-input"/>
              </label>
              <span className="text-sm text-gray-500">JPG, PNG or WebP up to 8MB.</span>
            </div>
            {photos.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-3">
                {photos.map(p => (
                  <div key={p} className="relative">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="" className="w-24 h-24 object-cover rounded-lg border border-gray-200"/>
                    <button type="button" onClick={() => setPhotos(photos.filter(x => x !== p))} aria-label="Remove"
                      className="absolute -top-2 -right-2 bg-gray-900 text-white rounded-full p-1"><X className="w-3 h-3"/></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label>Links to websites, accessibility info, reviews or testimonials</Label>
            <div className="mt-2 space-y-2">
              {links.map((l, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={l} onChange={e => setLinks(links.map((x, j) => j === i ? e.target.value : x))}
                    placeholder="https://…" className="h-11 border-2 border-gray-300" data-testid={`evidence-link-${i}`}/>
                  {links.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => setLinks(links.filter((_, j) => j !== i))}><X className="w-4 h-4"/></Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setLinks([...links, ""])} className="rounded-full" data-testid="add-evidence-link">
                <Plus className="w-4 h-4 mr-1"/>Add another link
              </Button>
            </div>
          </div>
          <div>
            <Label>Evidence description <Star/></Label>
            <Textarea required rows={3} value={form.evidence_description}
              onChange={e => set("evidence_description", e.target.value)}
              placeholder="Please explain what your evidence demonstrates about the venue's accessibility."
              className="mt-1 border-2 border-gray-300" data-testid="nom-evidence-desc"/>
          </div>
        </SectionCard>

        {/* 4. Experience & Impact */}
        <SectionCard title="4. Experience & Impact">
          <div>
            <Label>Have you personally visited, used, worked at, or experienced this venue/organisation? <Star/></Label>
            <RadioGroup value={form.has_visited === null ? "" : (form.has_visited ? "yes" : "no")}
              onValueChange={v => set("has_visited", v === "yes")} className="mt-2 grid grid-cols-2 gap-2">
              <RadioLabel value="yes" label="Yes" testId="nom-visited-yes" active={form.has_visited === true}/>
              <RadioLabel value="no" label="No" testId="nom-visited-no" active={form.has_visited === false}/>
            </RadioGroup>
          </div>
          {form.has_visited && (
            <div>
              <Label>Please describe your experience. <Star/></Label>
              <Textarea required={!!form.has_visited} rows={4} value={form.experience_description}
                onChange={e => set("experience_description", e.target.value)}
                className="mt-1 border-2 border-gray-300" data-testid="nom-experience"/>
            </div>
          )}
          <div>
            <Label>What makes this venue stand out for accessibility? <Star/></Label>
            <Textarea required rows={3} value={form.stand_out}
              onChange={e => set("stand_out", e.target.value)}
              className="mt-1 border-2 border-gray-300" data-testid="nom-standout"/>
          </div>
          <div>
            <Label>Why do you believe this venue should be included and rated in the accessibility app? <Star/></Label>
            <Textarea required rows={3} value={form.reason_for_inclusion}
              onChange={e => set("reason_for_inclusion", e.target.value)}
              className="mt-1 border-2 border-gray-300" data-testid="nom-reason"/>
          </div>
        </SectionCard>

        {/* 5. About the Nominator */}
        <SectionCard title="5. About You">
          <div>
            <Label>Your connection to the venue</Label>
            <Select value={form.nominator_connection || ""} onValueChange={v => set("nominator_connection", v)}>
              <SelectTrigger className="mt-1 h-12 border-2 border-gray-300" data-testid="nom-connection">
                <SelectValue placeholder="Select one" />
              </SelectTrigger>
              <SelectContent>
                {NOMINATOR_CONNECTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Would you be happy for us to contact you for further information?</Label>
            <RadioGroup value={form.nominator_can_contact === null ? "" : (form.nominator_can_contact ? "yes" : "no")}
              onValueChange={v => set("nominator_can_contact", v === "yes")} className="mt-2 grid grid-cols-2 gap-2">
              <RadioLabel value="yes" label="Yes" testId="nom-contact-yes" active={form.nominator_can_contact === true}/>
              <RadioLabel value="no" label="No" testId="nom-contact-no" active={form.nominator_can_contact === false}/>
            </RadioGroup>
          </div>
        </SectionCard>

        {/* 6. Consent */}
        <SectionCard title="6. Consent">
          <ConsentRow checked={consent.accuracy} onChange={v => setConsent(c => ({...c, accuracy: v}))} testId="consent-accuracy"
            text="I confirm that the information provided is accurate to the best of my knowledge."/>
          <ConsentRow checked={consent.understanding} onChange={v => setConsent(c => ({...c, understanding: v}))} testId="consent-understanding"
            text="I understand that submitting a venue does not guarantee a particular accessibility rating."/>
          <ConsentRow checked={consent.review} onChange={v => setConsent(c => ({...c, review: v}))} testId="consent-review"
            text="I consent to the information and evidence being reviewed for inclusion in the accessibility rating app."/>
        </SectionCard>

        <Button type="submit" disabled={submitting || !allConsent}
          className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-base disabled:bg-gray-300" data-testid="nom-submit">
          {submitting ? "Submitting…" : "Submit nomination"}
        </Button>
        {!allConsent && <p className="text-sm text-gray-500 text-center">Tick all three consent statements to enable submission.</p>}
      </form>
    </div>
  );
}

function Star() { return <span className="text-blue-700" aria-label="required">*</span>; }

function SectionCard({ title, children }) {
  return (
    <Card className="border-2 border-gray-900 rounded-xl">
      <CardContent className="p-6 space-y-5">
        <h2 className="font-heading text-xl font-semibold text-gray-900">{title}</h2>
        {children}
      </CardContent>
    </Card>
  );
}

function RadioLabel({ value, label, testId, active }) {
  return (
    <label className={`flex items-center gap-2 border-2 rounded-lg p-3 cursor-pointer ${active ? "border-blue-600 bg-blue-50" : "border-gray-300"}`}>
      <RadioGroupItem value={value} data-testid={testId}/>
      <span className="font-medium">{label}</span>
    </label>
  );
}

function ConsentRow({ checked, onChange, testId, text }) {
  return (
    <label className={`flex items-start gap-3 border-2 rounded-lg p-3 cursor-pointer ${checked ? "border-blue-600 bg-blue-50" : "border-gray-300"}`}>
      <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testId} className="mt-0.5"/>
      <span className="text-sm text-gray-800">{text}</span>
    </label>
  );
}
